import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { getNotifications, postNotificationsRead, postNotificationsReadAll } from '@/api/common';
import { Empty, ErrorBox, Loading, Pager, fmtDateTime, paged, PageTitle } from '../ui';

interface Row { id: number; type: string; title: string; body: string; read: boolean; at: string }

export function NotificationsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [unread, setUnread] = useState(false);
  const q = useQuery({ queryKey: ['parent-notifications', page, unread], queryFn: () => paged<Row>(getNotifications({ page, ...(unread ? { unread: 1 } : {}) })) });
  const refresh = () => qc.invalidateQueries({ predicate: (x) => ['parent-notifications', 'parent-home', 'notifications-unread'].includes(String(x.queryKey[0])) });
  const read = useMutation({ mutationFn: (id: number) => postNotificationsRead(id), onSuccess: refresh });
  const readAll = useMutation({ mutationFn: () => postNotificationsReadAll(), onSuccess: refresh });

  return (
    <div className="space-y-5">
      <PageTitle kicker="Me" title="Notifications" />
      <Card action={<div className="flex gap-2"><Button variant="ghost" onClick={() => { setUnread((v) => !v); setPage(1); }}>{unread ? 'Show all' : 'Unread only'}</Button><Button variant="ghost" disabled={readAll.isPending} onClick={() => readAll.mutate()}>Mark all read</Button></div>}>
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data?.data.length ? <Empty text="You're all caught up." /> : (
          <>
            <ul className="divide-y">
              {q.data.data.map((n) => (
                <li key={n.id} className="py-3 flex justify-between gap-4">
                  <div><div className={n.read ? '' : 'font-semibold'}>{n.title}</div><div className="text-sm text-slate-500">{n.body}</div><div className="text-xs text-slate-400 mt-1">{fmtDateTime(n.at)}</div></div>
                  {!n.read && <Button variant="ghost" onClick={() => read.mutate(n.id)}>Mark read</Button>}
                </li>
              ))}
            </ul>
            <Pager meta={q.data.meta} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
